import { AnimatePresence, m, useDragControls } from "framer-motion";
import { lazy, Suspense, useCallback, useEffect, useId, useState } from "react";
import type { ElectricalValidationResult } from "../../../domain/electrical/validateElectricalWiring";
import type { WeightResult } from "../../../domain/weight/calculateWeight";
import type {
	MaterialPhaseGroup,
	MaterialRow,
	MaterialViewMode,
} from "../../../engines/types";
import { formatPrice, useMarket, useT } from "../../../i18n";
import type { BuilderRunningCostSummary } from "../runningCostSummary";

const MaterialPane = lazy(() =>
	import("../../../components/builder/tabs/MaterialPane").then(
		({ MaterialPane }) => ({ default: MaterialPane }),
	),
);

interface FloatingSpecPillProps {
	estimatedTotal: number;
	floorArea: number;
	calcData: {
		materials: MaterialRow[];
		materialsByPhase?: MaterialPhaseGroup[];
		weight?: WeightResult;
		electricalBom?: ElectricalValidationResult;
	};
	materialViewMode: MaterialViewMode;
	onMaterialViewModeChange: (mode: MaterialViewMode) => void;
	onJumpToPricing: () => void;
	/** Absent when the design cannot support a running-cost estimate. */
	runningCost?: BuilderRunningCostSummary | null;
}

/* Height handled by CSS max-height on .spec-drawer */

const overlayVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
};

const drawerVariants = {
	hidden: { y: "100%" },
	visible: {
		y: 0,
		transition: {
			type: "spring" as const,
			damping: 32,
			stiffness: 380,
			mass: 0.8,
		},
	},
	exit: {
		y: "100%",
		transition: {
			type: "spring" as const,
			damping: 40,
			stiffness: 400,
			mass: 0.6,
		},
	},
};

export function FloatingSpecPill({
	estimatedTotal,
	floorArea,
	calcData,
	materialViewMode,
	onMaterialViewModeChange,
	onJumpToPricing,
	runningCost,
}: FloatingSpecPillProps) {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const dragControls = useDragControls();
	const runningHeadingId = useId();

	const toggleDrawer = useCallback(() => {
		setDrawerOpen((prev) => !prev);
	}, []);

	const closeDrawer = useCallback(() => {
		setDrawerOpen(false);
	}, []);

	// Close on Escape
	useEffect(() => {
		if (!drawerOpen) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeDrawer();
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [drawerOpen, closeDrawer]);

	const t = useT();
	const market = useMarket();
	const formattedCost = formatPrice(estimatedTotal, market);
	const formattedArea = floorArea.toFixed(1);
	// State first, the call to action last — the running-cost sentence belongs
	// with the other values, not after "click for the material overview".
	const pillAriaLabel = [
		t.specPill_ariaLabel
			.replaceAll("{cost}", formattedCost)
			.replaceAll("{area}", formattedArea),
		runningCost?.ariaSummary,
		t.specPill_ariaLabelAction,
	]
		.filter(Boolean)
		.join(" ");

	return (
		<>
			{/* ── Floating Pill ── */}
			<AnimatePresence>
				{!drawerOpen && (
					<m.button
						type="button"
						className="floating-spec-pill"
						onClick={toggleDrawer}
						aria-label={pillAriaLabel}
						aria-expanded={drawerOpen}
						aria-controls="spec-drawer"
						initial={{ opacity: 0, y: 20, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 10, scale: 0.97 }}
						transition={{ type: "spring", damping: 28, stiffness: 340 }}
					>
						<span className="floating-spec-pill__cost">
							{t.specPill_est} {formattedCost}
						</span>
						<span className="floating-spec-pill__divider" aria-hidden="true">
							•
						</span>
						<span className="floating-spec-pill__area">{formattedArea} m²</span>
						{runningCost && (
							<span className="floating-spec-pill__heat">
								<span
									className="floating-spec-pill__divider"
									aria-hidden="true"
								>
									•
								</span>
								{t.specPill_heatUp.replace("{time}", runningCost.heatUpLabel)}
							</span>
						)}
						<svg
							className="floating-spec-pill__chevron"
							width="14"
							height="14"
							viewBox="0 0 14 14"
							fill="none"
							aria-hidden="true"
						>
							<path
								d="M3.5 8.75L7 5.25L10.5 8.75"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</m.button>
				)}
			</AnimatePresence>

			{/* ── Drawer Overlay + Panel ── */}
			<AnimatePresence>
				{drawerOpen && (
					<>
						<m.div
							className="spec-drawer-overlay"
							variants={overlayVariants}
							initial="hidden"
							animate="visible"
							exit="hidden"
							transition={{ duration: 0.25 }}
							onClick={closeDrawer}
							aria-hidden="true"
						/>

						<m.aside
							id="spec-drawer"
							className="spec-drawer"
							role="dialog"
							aria-label={t.drawer_eyebrow}
							aria-modal="true"
							variants={drawerVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
							drag="y"
							dragControls={dragControls}
							dragConstraints={{ top: 0, bottom: 0 }}
							dragElastic={{ top: 0.05, bottom: 0.4 }}
							onDragEnd={(_e, info) => {
								// Close if swiped down far enough or fast enough
								if (info.offset.y > 100 || info.velocity.y > 500) {
									closeDrawer();
								}
							}}
						>
							{/* Drag handle */}
							<div
								className="spec-drawer__handle-bar"
								onPointerDown={(e) => dragControls.start(e)}
							>
								<div className="spec-drawer__handle" />
							</div>

							{/* Header */}
							<div className="spec-drawer__header">
								<div className="spec-drawer__header-info">
									<p className="spec-drawer__eyebrow">{t.drawer_eyebrow}</p>
									<div className="spec-drawer__summary-row">
										<span className="spec-drawer__cost">{formattedCost}</span>
										<span className="spec-drawer__area">
											{formattedArea} m²
										</span>
									</div>
								</div>
								<button
									type="button"
									className="spec-drawer__close"
									onClick={closeDrawer}
									aria-label={t.drawer_closeAriaLabel}
								>
									<svg
										width="18"
										height="18"
										viewBox="0 0 18 18"
										fill="none"
										aria-hidden="true"
									>
										<path
											d="M4.5 4.5L13.5 13.5M13.5 4.5L4.5 13.5"
											stroke="currentColor"
											strokeWidth="1.5"
											strokeLinecap="round"
										/>
									</svg>
								</button>
							</div>

							{/* Live running cost — heat-up time and cost per session,
							    tracking the design as it changes. */}
							{runningCost && (
								<section
									className="spec-drawer__running"
									aria-labelledby={runningHeadingId}
								>
									<p
										className="spec-drawer__running-heading"
										id={runningHeadingId}
									>
										{t.runningCost_drawerHeading}
									</p>
									<dl className="spec-drawer__running-stats">
										<div className="spec-drawer__running-stat">
											<dt>{t.runningCost_heatUpLabel}</dt>
											<dd>{runningCost.heatUpLabel}</dd>
										</div>
										<div className="spec-drawer__running-stat">
											<dt>{t.runningCost_sessionCostLabel}</dt>
											<dd>{runningCost.sessionCostLabel}</dd>
										</div>
										<div className="spec-drawer__running-stat">
											<dt>{t.runningCost_monthlyCostLabel}</dt>
											<dd>{runningCost.monthlyCostLabel}</dd>
										</div>
									</dl>
									<p className="spec-drawer__running-note">
										{runningCost.costNote}
									</p>
								</section>
							)}

							{/* Content: full Material Audit */}
							<div className="spec-drawer__content">
								<Suspense
									fallback={<div role="status">{t.common_loading}</div>}
								>
									<MaterialPane
										rows={calcData.materials}
										materialsByPhase={calcData.materialsByPhase}
										weight={calcData.weight}
										electricalBom={calcData.electricalBom}
										viewMode={materialViewMode}
										onViewModeChange={onMaterialViewModeChange}
										onJumpToPricing={onJumpToPricing}
									/>
								</Suspense>
							</div>
						</m.aside>
					</>
				)}
			</AnimatePresence>
		</>
	);
}
